import type { OktaAuth } from "@okta/okta-auth-js";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import {
  API_GATEWAY_ID_CUSTOMER_SERVICE,
  API_GATEWAY_ID_PROD_SERVICE,
  AUTH_ROOT_URL,
  INVMGMT_SERVICE_URL,
  SERVICE_URL,
} from "../utils/env.config";

let oktaClient: OktaAuth | null = null;
let logoutInFlight = false;
let renewInFlight = false;

export const attachAuthClient = (client: OktaAuth) => {
  oktaClient = client;
};

/** Setup an API instance */
const prodServiceGatewayHeader = API_GATEWAY_ID_PROD_SERVICE && {
  "x-apigw-api-id": API_GATEWAY_ID_PROD_SERVICE,
};

const customerServiceGatewayHeader = API_GATEWAY_ID_CUSTOMER_SERVICE && {
  "x-apigw-api-id": API_GATEWAY_ID_CUSTOMER_SERVICE,
};

export const api = axios.create({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    ...prodServiceGatewayHeader,
  },
  withCredentials: true,
});

export const api360 = axios.create({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    ...customerServiceGatewayHeader,
  },
  withCredentials: true,
});

const getAccessToken = async (): Promise<string | null> => {
  if (oktaClient) {
    try {
      const t = await oktaClient.tokenManager.get("accessToken");
      const v = (t as any)?.accessToken ?? (t as any)?.value ?? null;
      return v ?? null;
    } catch {
      return null;
    }
  }
  const raw = localStorage.getItem("okta-token-storage");
  if (!raw) return null;
  try {
    const j = JSON.parse(raw);
    return j?.accessToken?.accessToken ?? j?.accessToken?.value ?? null;
  } catch {
    return null;
  }
};

const tryRenewOnce = async (): Promise<boolean> => {
  if (!oktaClient) return false;
  if (renewInFlight) {
    await new Promise(res => setTimeout(res, 300));
    const t = await getAccessToken();
    return !!t;
  }
  renewInFlight = true;
  try {
    await oktaClient.tokenManager.renew("accessToken");
    return true;
  } catch {
    return false;
  } finally {
    renewInFlight = false;
  }
};

/**
 * Adds authorization headers to API calls
 * @param {AxiosRequestConfig} request
 */
const authInterceptor = async (request: AxiosRequestConfig) => {
  const token = await getAccessToken();
  if (token && request.headers) {
    request.headers.Authorization = `Bearer ${token}`;
  }
  return request;
};

/** Add interceptor */
api.interceptors.request.use(config => {
  if (!config.baseURL) {
    config.baseURL = INVMGMT_SERVICE_URL;
  }
  return config;
});

api360.interceptors.request.use(config => {
  if (!config.baseURL) {
    config.baseURL = SERVICE_URL;
  }
  return config;
});

/**
 * Axios response interceptors
 * @param {AxiosResponse} response
 */
const isJsonContentType = (response: AxiosResponse) => {
  const ct = (response.headers?.["content-type"] || response.headers?.["Content-Type"] || "").toString().toLowerCase();
  return ct.includes("application/json");
};

const isAcceptJson = (config: AxiosRequestConfig) => {
  const accept = (config.headers as any)?.Accept || (config.headers as any)?.accept;
  return typeof accept === "string" && accept.toLowerCase().includes("application/json");
};

const responseInterceptor = async (response: AxiosResponse) => {
  if (response?.status === 200) {
    const expectJson = isAcceptJson(response.config);
    if (expectJson && !isJsonContentType(response)) {
      const renewed = await tryRenewOnce();
      if (!renewed) {
        await forceLogout();
        return Promise.reject(response);
      }
      const token = await getAccessToken();
      const retryHeaders = {
        ...(response.config.headers || {}),
      };
      if (token) {
        (retryHeaders as any).Authorization = `Bearer ${token}`;
      }
      try {
        return await axios({ ...response.config, headers: retryHeaders });
      } catch (retryErr) {
        await forceLogout();
        return Promise.reject((retryErr as any)?.response ?? retryErr);
      }
    }
  }
  return response;
};

const forceLogout = async () => {
  if (logoutInFlight) return;
  logoutInFlight = true;
  try {
    if (oktaClient) {
      await oktaClient.signOut();
    } else {
      window.location.assign(AUTH_ROOT_URL as string);
    }
  } finally {
    logoutInFlight = false;
  }
};

const isStaticResourceRequest = (config: AxiosRequestConfig): boolean => {
  const urlPart = (config.url || "").toString();
  const base = (config.baseURL || "").toString();
  const fullUrl = base ? new URL(urlPart, base).toString() : urlPart;

  try {
    const u = new URL(fullUrl, window.location.origin);
    const pathname = u.pathname.toLowerCase();

    const staticExts = [
      ".js",
      ".css",
      ".png",
      ".jpg",
      ".jpeg",
      ".gif",
      ".svg",
      ".ico",
      ".bmp",
      ".webp",
      ".woff",
      ".woff2",
      ".ttf",
      ".otf",
      ".eot",
      ".map",
      ".json",
      ".txt",
      ".html",
    ];

    if (staticExts.some(ext => pathname.endsWith(ext))) return true;

    if (
      pathname.startsWith("/static/") ||
      pathname.startsWith("/assets/") ||
      pathname.startsWith("/public/")
    ) {
      return true;
    }

    const accept = (config.headers as any)?.Accept || (config.headers as any)?.accept;
    if (typeof accept === "string") {
      const a = accept.toLowerCase();
      if (
        a.includes("text/html") ||
        a.includes("text/css") ||
        a.includes("image/") ||
        a.includes("application/javascript")
      ) {
        return true;
      }
    }
  } catch {}

  return false;
};

/**
 * Axios error interceptor
 * @param {AxiosError} axiosError
 */
const errorInterceptor = async (axiosError: AxiosError) => {
  const status = axiosError?.response?.status;
  const originalConfig: any = axiosError?.config ?? {};
  const hasRetried = originalConfig.__retried === true;

  const isStatic = isStaticResourceRequest(originalConfig);

  if (status && (status === 401 || status === 403)) {
    if (isStatic) {
      await forceLogout();
      return Promise.reject(axiosError.response ?? axiosError);
    }

    if (status === 401) {
      const renewed = await tryRenewOnce();
      if (!renewed) {
        await forceLogout();
        return Promise.reject(axiosError.response ?? axiosError);
      }

      if (!hasRetried) {
        originalConfig.__retried = true;
        const token = await getAccessToken();
        if (token) {
          originalConfig.headers = {
            ...(originalConfig.headers || {}),
            Authorization: `Bearer ${token}`,
          };
        }
        try {
          return await axios(originalConfig);
        } catch (retryErr) {
          return Promise.reject((retryErr as any)?.response ?? retryErr);
        }
      }

      return Promise.reject(axiosError.response ?? axiosError);
    }

    if (status === 403) {
      return Promise.reject(axiosError.response ?? axiosError);
    }
  }

  if (axiosError && axiosError.response) {
    return Promise.reject(axiosError.response);
  }
  return Promise.reject(axiosError);
};

api.interceptors.request.use(authInterceptor);
api.interceptors.response.use(undefined, errorInterceptor);

api360.interceptors.request.use(authInterceptor);
api360.interceptors.response.use(undefined, errorInterceptor);
